/**
 * DocumentList — Document browser using listr-lib + selectr-ui.
 *
 * Composes:
 * - listr-lib (ListrManager): Dynamic list management, filtering, caching
 * - selectr-ui (Selectr): Selection state and dropdown/picker UI
 * - widgetr-lib (Input, Badge): Search input, status badges
 *
 * This component is a thin wiring layer — all list logic lives in listr,
 * all selection logic lives in selectr.
 */

import React, { useState, useCallback } from 'react';
import { useExpressrContext } from '../context/ExpressrContext.js';
import type { DocumentStatus } from '../types.js';

const STATUS_LABELS: Record<DocumentStatus, string> = {
  draft: 'Draft',
  review: 'In Review',
  approved: 'Approved',
  published: 'Published',
  archived: 'Archived',
};

/**
 * DocumentList renders the document sidebar.
 *
 * In a full integration, this would use:
 * - ListrManager.createList() to define the document list with query/filter config
 * - ListrManager.queryList() to fetch/filter documents
 * - Selectr component for single-select document picking
 * - Input from widgetr-lib for the search field
 * - Badge from widgetr-lib for status indicators
 *
 * Currently wires to ExpressrContext state with the same interface
 * so these packages can be swapped in as they gain persistence.
 */
export function DocumentList() {
  const {
    filteredDocuments,
    state,
    selectDocument,
    createDocument,
    deleteDocument,
    setFilter,
    setSidePanel,
  } = useExpressrContext();

  const [showNewForm, setShowNewForm] = useState(false);
  const [newTitle, setNewTitle] = useState('');
  const [statusFilter, setStatusFilter] = useState<DocumentStatus | 'all'>('all');

  // listr-lib pattern: filter via query definition
  const displayed = statusFilter === 'all'
    ? filteredDocuments
    : filteredDocuments.filter((d) => d.status === statusFilter);

  const handleCreate = useCallback(() => {
    if (!newTitle.trim()) return;
    createDocument(newTitle.trim());
    setNewTitle('');
    setShowNewForm(false);
    setSidePanel('none');
  }, [newTitle, createDocument, setSidePanel]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter') handleCreate();
    if (e.key === 'Escape') setShowNewForm(false);
  }, [handleCreate]);

  const formatDate = (date: Date) => {
    const d = new Date(date);
    const now = new Date();
    const diff = now.getTime() - d.getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return 'just now';
    if (mins < 60) return `${mins}m ago`;
    const hours = Math.floor(mins / 60);
    if (hours < 24) return `${hours}h ago`;
    const days = Math.floor(hours / 24);
    if (days < 7) return `${days}d ago`;
    return d.toLocaleDateString();
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Header — widgetr Input for search */}
      <div style={{ padding: '12px 16px', borderBottom: '1px solid var(--border, #2a2a4a)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
          <span style={{ fontWeight: 600, fontSize: 14 }}>Documents</span>
          <button onClick={() => setShowNewForm(true)}>+ New</button>
        </div>
        {/* Would use: <Input from widgetr-lib value={state.filter} onChange={setFilter} /> */}
        <input
          type="text"
          value={state.filter}
          onChange={(e) => setFilter(e.target.value)}
          placeholder="Search documents..."
        />
        {/* Would use: <Selectr items={statusOptions} value={statusFilter} onChange={setStatusFilter} /> */}
        <div style={{ display: 'flex', gap: 4, marginTop: 8 }}>
          {(['all', 'draft', 'review', 'published'] as const).map((s) => (
            <button
              key={s}
              onClick={() => setStatusFilter(s)}
              data-active={statusFilter === s}
            >
              {s === 'all' ? 'All' : STATUS_LABELS[s]}
            </button>
          ))}
        </div>
      </div>

      {/* New document form */}
      {showNewForm && (
        <div style={{ padding: '8px 16px', borderBottom: '1px solid var(--border, #2a2a4a)' }}>
          <input
            type="text"
            value={newTitle}
            onChange={(e) => setNewTitle(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Document title..."
            autoFocus
          />
          <div style={{ display: 'flex', gap: 4, marginTop: 6 }}>
            <button onClick={handleCreate}>Create</button>
            <button onClick={() => setShowNewForm(false)}>Cancel</button>
          </div>
        </div>
      )}

      {/* Document list — would use listr-lib ListrProvider + selectr-ui Selectr */}
      <div style={{ flex: 1, overflow: 'auto' }}>
        {displayed.length === 0 ? (
          <div style={{ padding: 16, textAlign: 'center', opacity: 0.5, fontSize: 12 }}>
            {state.filter ? 'No documents match your search' : 'No documents yet.'}
          </div>
        ) : (
          displayed.map((doc) => (
            <div
              key={doc.id}
              onClick={() => selectDocument(doc.id)}
              data-active={doc.id === state.activeDocumentId}
              style={{ padding: '10px 16px', cursor: 'pointer', borderBottom: '1px solid var(--border-light, #1e1e38)' }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontSize: 13, fontWeight: doc.id === state.activeDocumentId ? 600 : 400 }}>
                  {doc.title}
                </span>
                {/* Would use: <Badge from widgetr-lib variant={doc.status} /> */}
                <span data-status={doc.status} style={{ fontSize: 10 }}>{STATUS_LABELS[doc.status]}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 4, fontSize: 11, opacity: 0.6 }}>
                <span>{doc.wordCount > 0 ? `${doc.wordCount} words` : 'Empty'}</span>
                <span>{formatDate(doc.updatedAt)}</span>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Footer */}
      <div style={{ padding: '8px 16px', borderTop: '1px solid var(--border, #2a2a4a)', fontSize: 11, opacity: 0.6 }}>
        {displayed.length} document{displayed.length !== 1 ? 's' : ''}
      </div>
    </div>
  );
}